import {
  ASSETS,
  NATION_SHORT,
  CashUnitMap,
  NationShortType,
  AssetType,
} from "@dominoapp/core-utils";

/**
 * @see https://www.notion.so/weffwd/faba7130cb5c4e978585c312d624f0da#3d573453d64f4135883656bbe4ae3a3c
 */

export const getCashUnitText = (key: keyof typeof CashUnitMap) =>
  CashUnitMap[key] || CashUnitMap[NATION_SHORT.KR];

// [홈 / 분석화면 (수익/비중/배당) ]

export const HOME_NAME_KR_FORMAT_STOCKS_NATION: Partial<NationShortType[]> = [
  NATION_SHORT.KR,
  NATION_SHORT.CN,
  NATION_SHORT.HK,
];

export const HOME_SYMBOL_ALTER_FORMAT_STOCKS_NATION: Partial<
  NationShortType[]
> = [
  NATION_SHORT.US,
  NATION_SHORT.GB,
  NATION_SHORT.FR,
  NATION_SHORT.DE,
  NATION_SHORT.NL,
];

export const HOME_NAME_KR_FORMAT_ASSETS: Partial<AssetType[]> = [
  ASSETS.CRYPTO,
  ASSETS.FUND,
  ASSETS.KR_APT,
];

/**
 * [홈 / 분석화면 (수익/비중/배당) / 종목 상세 (자산 구성)] 의 종목명
 * @param {AssetType} assetClass
 * @param {NationShortType} nation
 * @param {string} nameKr
 * @param {string} symbol
 * @param {string} symbolAlter
 * @returns {string} 표기법에 맞는 문자열
 * @example
 * // returns "TSLA"
 * tickerFormat("STOCK","US","테슬라","TSLA","");
 */
export const homeNameFormat = (
  assetClass: AssetType,
  nation: NationShortType,
  nameKr: string,
  symbol: string,
  symbolAlter: string,
) => {
  if (assetClass === ASSETS.STOCK) {
    if (HOME_NAME_KR_FORMAT_STOCKS_NATION.includes(nation)) {
      return nameKr;
    }

    if (HOME_SYMBOL_ALTER_FORMAT_STOCKS_NATION.includes(nation)) {
      return symbolAlter || symbol;
    }
  }

  if (HOME_NAME_KR_FORMAT_ASSETS.includes(assetClass)) {
    return nameKr;
  }

  return nameKr;
};

// [랭킹 / 검색 / 관심]

export const NAME_KR_FORMAT_ASSETS: Partial<AssetType[]> = [
  ASSETS.CRYPTO,
  ASSETS.FUND,
  ASSETS.KR_APT,
  ASSETS.INDEX,
  ASSETS.COMMODITY,
  ASSETS.EXCHANGE_RATE,
  ASSETS.GOLD,
  ASSETS.GOV_BONDS,
  ASSETS.INDEX_FUTURES,
  ASSETS.INTEREST_RATE,
];

export const NAME_SYMBOL_ALTER_FORMAT_STOCKS_NATION: Partial<
  NationShortType[]
> = [
  NATION_SHORT.US,
  NATION_SHORT.GB,
  NATION_SHORT.FR,
  NATION_SHORT.DE,
  NATION_SHORT.NL,
];

/**
 * 랭킹 / 검색 / 관심 리스트의 종목명
 * @param {AssetType} assetClass
 * @param {NationShortType} nation
 * @param {string} nameKr
 * @param {string} nameEn
 * @param {string} symbol
 * @param {string} symbolAlter
 * @returns {string} 표기법에 맞는 문자열
 * @example
 * // returns "TSLA"
 * tickerFormat("STOCK","US","테슬라","Tsla Inc","TSLA","");
 */
export const searchNameFormat = (
  assetClass: AssetType,
  nation: NationShortType,
  nameKr: string,
  nameEn: string,
  symbol: string,
  symbolAlter: string,
) => {
  if (assetClass === ASSETS.STOCK) {
    if (NAME_SYMBOL_ALTER_FORMAT_STOCKS_NATION.includes(nation)) {
      return symbolAlter || symbol;
    }

    return nameKr || nameEn;
  }

  if (NAME_KR_FORMAT_ASSETS.includes(assetClass)) {
    return nameKr;
  }

  return nameKr;
};

export const TICKER_SYMBOL_FORMAT_STOCKS_NATION: Partial<NationShortType[]> = [
  NATION_SHORT.KR,
];

export const TICKER_SYMBOL_ALTER_FORMAT_STOCKS_NATION: Partial<
  NationShortType[]
> = [NATION_SHORT.HK, NATION_SHORT.CN];

export const TICKER_SYMBOL_FORMAT_ASSETS: Partial<AssetType[]> = [
  ASSETS.CRYPTO,
  ASSETS.INDEX,
  ASSETS.COMMODITY,
  ASSETS.EXCHANGE_RATE,
  ASSETS.GOLD,
  ASSETS.GOV_BONDS,
  ASSETS.INDEX_FUTURES,
  ASSETS.INTEREST_RATE,
];

export const TICKER_NAMEKR_FORMAT_STOCKS_NATION: Partial<NationShortType[]> = [
  NATION_SHORT.US,
  NATION_SHORT.GB,
  NATION_SHORT.FR,
  NATION_SHORT.DE,
  NATION_SHORT.NL,
];

export const TICKER_SYMBOL_ALTER_FORMAT_ASSETS: Partial<AssetType[]> = [
  ASSETS.FUND,
  ASSETS.KR_APT,
];

/**
 * 랭킹 / 검색 / 관심 리스트의 티커명
 * @param {AssetType} assetClass
 * @param {NationShortType} nation
 * @param {string} nameKr
 * @param {string} nameEn
 * @param {string} symbol
 * @param {string} symbolAlter
 * @returns {string} 표기법에 맞는 문자열
 * @example
 * // returns "테슬라"
 * tickerFormat("STOCK","US","테슬라","Tsla Inc","TSLA","");
 */
export const searchSymbolFormat = (
  assetClass: AssetType,
  nation: NationShortType,
  nameKr: string,
  nameEn: string,
  symbol: string,
  symbolAlter: string,
) => {
  if (assetClass === ASSETS.STOCK) {
    if (TICKER_SYMBOL_FORMAT_STOCKS_NATION.includes(nation)) {
      return symbol;
    }

    if (TICKER_SYMBOL_ALTER_FORMAT_STOCKS_NATION.includes(nation)) {
      return symbolAlter;
    }

    if (TICKER_NAMEKR_FORMAT_STOCKS_NATION.includes(nation)) {
      return nameKr || nameEn;
    }
  }

  if (TICKER_SYMBOL_FORMAT_ASSETS.includes(assetClass)) {
    return symbol;
  }

  if (TICKER_SYMBOL_ALTER_FORMAT_ASSETS.includes(assetClass)) {
    return symbolAlter;
  }

  return symbol;
};

// [종목 상세]

/**
 * 종목 상세의 종목명
 * @param {string} nameKr
 * @param {string} nameEn
 * @returns {string} 표기법에 맞는 문자열
 * @example
 * // returns "테슬라"
 * tickerFormat("테슬라", "Tsla Inc");
 */
export const stockDetailNameFormat = (nameKr: string, nameEn: string) => {
  return nameKr || nameEn;
};

export const SYMBOL_ALTER_표기_주식_국가: Partial<NationShortType[]> = [
  NATION_SHORT.HK,
  NATION_SHORT.CN,
  NATION_SHORT.US,
  NATION_SHORT.GB,
  NATION_SHORT.FR,
  NATION_SHORT.DE,
  NATION_SHORT.NL,
];

export const SYMBOL_ALTER_표기_에셋: Partial<AssetType[]> = [
  ASSETS.CRYPTO,
  ASSETS.INDEX,
  ASSETS.KR_APT,
  ASSETS.COMMODITY,
  ASSETS.EXCHANGE_RATE,
  ASSETS.GOLD,
  ASSETS.GOV_BONDS,
  ASSETS.INDEX_FUTURES,
  ASSETS.INTEREST_RATE,
];

/**
 * 종목 상세의 티커명
 * @param {AssetType} assetClass
 * @param {NationShortType} nation
 * @param {string} symbol
 * @param {string} symbolAlter
 * @returns {string} 표기법에 맞는 문자열
 * @example
 * // returns "TSLA"
 * tickerFormat("STOCK","US","TSLA","");
 */
export const stockDetailSymbolFormat = (
  assetClass: AssetType,
  nation: NationShortType,
  symbol: string,
  symbolAlter: string,
) => {
  if (assetClass === ASSETS.FUND) return "";

  if (assetClass === ASSETS.INDEX_FUTURES) {
    return symbolAlter || symbol;
  }

  if (assetClass === ASSETS.STOCK) {
    if (SYMBOL_ALTER_표기_주식_국가.includes(nation))
      return symbolAlter || symbol;

    return symbol;
  }

  if (SYMBOL_ALTER_표기_에셋.includes(assetClass)) {
    return symbolAlter || symbol || "";
  }

  return symbol;
};

// [내 자산]

export const assetNameFormat = (nameKr: string, nameEn: string) => {
  return nameKr || nameEn;
};

export const ASSET_SYMBOL_표기_주식_국가: Partial<NationShortType[]> = [
  NATION_SHORT.KR,
];
export const ASSET_SYMBOL_표기_에셋: Partial<AssetType[]> = [ASSETS.CRYPTO];

export const assetSymbolFormat = (
  assetClass: AssetType,
  nation: NationShortType,
  symbol: string,
  symbolAlter: string,
) => {
  if (assetClass === ASSETS.STOCK) {
    if (ASSET_SYMBOL_표기_주식_국가.includes(nation)) return symbol;
  }

  if (ASSET_SYMBOL_표기_에셋.includes(assetClass)) return symbol;

  return symbolAlter || symbol;
};
